import { z } from "zod";

export const apiErrorSchema = z.object({
  code: z.string().nullish(),
  message: z.string().nullish(),
  status: z.number().nullish(),
});

export const errorResponseSchema = z.object({
  message: z.string().nullish(),
  errors: z.array(apiErrorSchema).nullish(),
});

const alternateApiErrorSchema = z.object({
  errorCode: z.union([z.number(), z.string()]).nullish(),
  errorMessage: z.string().nullish(),
});

const alternateTopLevelErrorSchema = alternateApiErrorSchema;

const alternateNestedDataErrorSchema = z
  .object({
    data: z
      .object({
        errorCode: z.union([z.number(), z.string()]).nullish(),
        errorMessage: z.string().nullish(),
      })
      .passthrough(),
  })
  .passthrough();

function parseAlternateError(maybeError: ApiErrorObject): {
  code?: string | number | null | undefined;
  message?: string | null | undefined;
} | null {
  const nested = alternateNestedDataErrorSchema.safeParse(maybeError.body);
  if (nested.success) {
    const { errorCode, errorMessage } = nested.data.data;
    return { code: errorCode, message: errorMessage };
  }

  const top = alternateTopLevelErrorSchema.safeParse(maybeError.body);
  if (top.success) {
    const { errorCode, errorMessage } = top.data;
    return { code: errorCode, message: errorMessage };
  }

  return null;
}

// proper type for error objects that may come from api calls
export interface ApiErrorObject {
  body?: unknown;
  message?: string;
  [key: string]: unknown;
}

export const defaultErrorCode = "UNKNOWN";
export const defaultErrorMessage = "An unexpected error occurred";

export function getErrorMessage(
  maybeError: ApiErrorObject,
  fallbackMessage?: string,
): string {
  const errorMessage = fallbackMessage ?? defaultErrorMessage;

  const result = errorResponseSchema.safeParse(maybeError.body);
  if (!result.success) {
    const alt = parseAlternateError(maybeError);
    if (alt) {
      return (
        alt.message ??
        (typeof alt.code === "number" ? String(alt.code) : undefined) ??
        errorMessage
      );
    }
    return errorMessage;
  }

  const { message, errors } = result.data;
  return message ?? errors?.[0]?.message ?? errorMessage;
}

export interface ErrorMessageWithCode {
  message: string;
  code: string;
}

export function getErrorMessageWithCode(
  maybeError: ApiErrorObject,
  fallbackMessage?: string,
): ErrorMessageWithCode {
  const errorMessage = fallbackMessage ?? defaultErrorMessage;

  const result = errorResponseSchema.safeParse(maybeError.body);

  if (!result.success || !result.data.errors?.[0]) {
    const alt = parseAlternateError(maybeError);
    if (alt) {
      const codeValue = alt.code;
      const messageValue = alt.message;
      return {
        code:
          typeof codeValue === "number"
            ? String(codeValue)
            : (codeValue ?? defaultErrorCode),
        message: messageValue ?? errorMessage,
      };
    }

    return { code: defaultErrorCode, message: errorMessage };
  }

  const { message, code } = result.data.errors[0];

  return { code: code ?? defaultErrorCode, message: message ?? errorMessage };
}
